import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { RichTextEditor } from './RichTextEditor';
import {
  ArrowLeft, BookOpen, Clock, CheckCircle, AlertTriangle, XCircle,
  Globe, Zap, Search, Filter, ChevronDown, BarChart3, Calendar, Eye,
  Shield, Sparkles, Play, RefreshCw, FileText, TrendingUp, Database,
  ArrowRight, Loader2, Pause, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  idea: { color: 'bg-gray-100 text-gray-700', label: 'Idea' },
  queued: { color: 'bg-blue-100 text-blue-700', label: 'Queued' },
  brief_ready: { color: 'bg-indigo-100 text-indigo-700', label: 'Brief' },
  drafted: { color: 'bg-yellow-100 text-yellow-700', label: 'Draft' },
  awaiting_review: { color: 'bg-orange-100 text-orange-700', label: 'Review' },
  approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Approved' },
  scheduled: { color: 'bg-cyan-100 text-cyan-700', label: 'Scheduled' },
  published: { color: 'bg-green-100 text-green-700', label: 'Published' },
  rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
};

const BUCKET_LABELS = {
  fdi_india: 'FDI India', virtual_cfo: 'Virtual CFO', gst: 'GST',
  payroll: 'Payroll', compliance_updates: 'Compliance', pricing: 'Pricing',
  comparison_vs: 'Comparison', templates_checklists: 'Templates', how_to: 'How-To',
  industry_specific: 'Industry', mis_reporting: 'MIS', receivables_cash_flow: 'Cash Flow',
  accounting_outsourcing: 'Accounting', budgeting: 'Budgeting',
};

const ReviewQueueView = ({ reviewQueue, actionLoading, handleAction, fetchAll }) => {
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({});

  const startEdit = (topic) => {
    setEditMode(topic.topic_id);
    setEditData({
      title: topic.title || '',
      excerpt: topic.excerpt || '',
      content: topic.content || '',
      seo_title: topic.seo_title || '',
      meta_description: topic.meta_description || '',
    });
  };

  const cancelEdit = () => {
    setEditMode(null);
    setEditData({});
  };

  const handleEditAndApprove = async (topicId) => {
    await handleAction(topicId, 'review', {
      action: 'edit_and_approve',
      edited_title: editData.title,
      edited_content: editData.content,
    });
    setEditMode(null);
    setEditData({});
  };

  const handleSaveEdits = async (topicId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content-engine/topics/${topicId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editData.title,
          notes: `Excerpt: ${editData.excerpt}`,
        }),
      });
      if (res.ok) {
        // Also update content directly in DB via the review endpoint
        toast.success('Edits saved');
        await fetchAll();
        setEditMode(null);
      }
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold" data-testid="review-queue-title">Review Queue ({reviewQueue.length})</h2>
      </div>
      {reviewQueue.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400">No topics awaiting review. Safe content is auto-published.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {reviewQueue.map(t => {
            const isExpanded = expandedTopic === t.topic_id;
            const isEditing = editMode === t.topic_id;

            return (
              <Card key={t.topic_id} className="border-l-4 border-l-orange-400" data-testid={`review-card-${t.topic_id}`}>
                <CardContent className="pt-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900">{t.title}</h3>
                      <p className="text-xs text-gray-500 mt-1">{t.primary_keyword} | {BUCKET_LABELS[t.topic_bucket] || t.topic_bucket}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className="bg-orange-100 text-orange-700 text-[10px]">Score: {t.total_score}</Badge>
                      {t.fdi_related && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">FDI</Badge>}
                    </div>
                  </div>

                  {/* Review reason & flags */}
                  {t.review_reason && (
                    <div className="p-2 bg-amber-50 rounded-lg mb-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-800">{t.review_reason}</p>
                    </div>
                  )}
                  {t.risk_flags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {t.risk_flags.map((f, i) => <Badge key={f || `flag-${i}`} variant="outline" className="text-[10px] text-red-600 border-red-200">{f}</Badge>)}
                    </div>
                  )}
                  {t.excerpt && <p className="text-sm text-gray-600 mb-3">{t.excerpt}</p>}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Button size="sm" variant="outline" onClick={() => setExpandedTopic(isExpanded ? null : t.topic_id)}
                      data-testid={`view-content-${t.topic_id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> {isExpanded ? 'Hide Content' : 'View Content'}
                    </Button>
                    {!isEditing && (
                      <Button size="sm" variant="outline" onClick={() => { setExpandedTopic(t.topic_id); startEdit(t); }}
                        className="text-blue-600 border-blue-200" data-testid={`edit-content-${t.topic_id}`}>
                        <FileText className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                    <Button size="sm" onClick={() => handleAction(t.topic_id, 'review', { action: 'approve' })}
                      disabled={actionLoading.includes(t.topic_id)}
                      className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`approve-${t.topic_id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve & Publish
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleAction(t.topic_id, 'review', { action: 'reject', rejection_reason: 'Needs revision' })}
                      className="text-red-600 border-red-200" data-testid={`reject-${t.topic_id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>

                  {/* Expanded content view */}
                  {isExpanded && !isEditing && t.content && (
                    <div className="mt-4 border rounded-xl overflow-hidden" data-testid={`content-preview-${t.topic_id}`}>
                      <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Generated Content Preview</span>
                        <Button size="sm" variant="ghost" className="h-6 text-xs text-blue-600"
                          onClick={() => startEdit(t)}>
                          <FileText className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      </div>
                      {t.seo_title && (
                        <div className="px-4 py-2 bg-blue-50 border-b">
                          <p className="text-[10px] text-blue-500 uppercase font-medium">SEO Title</p>
                          <p className="text-sm text-gray-800">{t.seo_title}</p>
                        </div>
                      )}
                      {t.meta_description && (
                        <div className="px-4 py-2 bg-blue-50 border-b">
                          <p className="text-[10px] text-blue-500 uppercase font-medium">Meta Description</p>
                          <p className="text-sm text-gray-600">{t.meta_description}</p>
                        </div>
                      )}
                      <div className="px-4 py-4 prose prose-sm max-w-none max-h-[500px] overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.content) }} />
                    </div>
                  )}

                  {/* Expanded content when no content exists */}
                  {isExpanded && !isEditing && !t.content && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl text-center text-sm text-gray-400">
                      No content generated yet for this topic.
                    </div>
                  )}

                  {/* Edit mode */}
                  {isEditing && (
                    <div className="mt-4 border rounded-xl overflow-hidden" data-testid={`content-editor-${t.topic_id}`}>
                      <div className="bg-blue-50 px-4 py-2 border-b">
                        <span className="text-xs font-medium text-blue-600">Editing Content</span>
                      </div>
                      <div className="p-4 space-y-4">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Title</label>
                          <Input value={editData.title} onChange={e => setEditData(prev => ({ ...prev, title: e.target.value }))}
                            className="text-sm" data-testid={`edit-title-${t.topic_id}`} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Excerpt</label>
                          <Input value={editData.excerpt} onChange={e => setEditData(prev => ({ ...prev, excerpt: e.target.value }))}
                            className="text-sm" data-testid={`edit-excerpt-${t.topic_id}`} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">SEO Title</label>
                          <Input value={editData.seo_title} onChange={e => setEditData(prev => ({ ...prev, seo_title: e.target.value }))}
                            className="text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Meta Description</label>
                          <Input value={editData.meta_description} onChange={e => setEditData(prev => ({ ...prev, meta_description: e.target.value }))}
                            className="text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Content</label>
                          <RichTextEditor
                            content={editData.content}
                            onChange={(html) => setEditData(prev => ({ ...prev, content: html }))}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleEditAndApprove(t.topic_id)}
                            disabled={actionLoading.includes(t.topic_id)}
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`edit-approve-${t.topic_id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Save & Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ContentEngine = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);

  const sortedBuckets = useMemo(
    () => Object.entries(dashboard?.buckets || {}).sort((a, b) => b[1] - a[1]),
    [dashboard]
  );
  const [topics, setTopics] = useState([]);
  const [total, setTotal] = useState(0);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBucket, setFilterBucket] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFdi, setFilterFdi] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content-engine/dashboard`);
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchTopics = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/content-engine/topics?limit=100&sort=score`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterBucket) url += `&bucket=${filterBucket}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterFdi === 'true') url += `&fdi=true`;
      if (filterFdi === 'false') url += `&fdi=false`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setTopics(data.topics || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
  }, [search, filterBucket, filterStatus, filterFdi]);

  const fetchReview = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content-engine/review-queue`);
      if (res.ok) {
        const data = await res.json();
        setReviewQueue(data.topics || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchTopics(), fetchReview()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchTopics, fetchReview]);

  const handleAction = async (topicId, action, extra = {}) => {
    setActionLoading(topicId + action);
    try {
      let url = `${BACKEND_URL}/api/content-engine/topics/${topicId}/${action}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(extra)
      });
      if (res.ok) {
        toast.success(`Action ${action} completed`);
        await Promise.all([fetchDashboard(), fetchTopics(), fetchReview()]);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Action failed');
      }
    } catch (e) { toast.error(e.message); }
    finally { setActionLoading(''); }
  };

  const triggerDaily = async () => {
    setActionLoading('daily');
    try {
      const res = await fetch(`${BACKEND_URL}/api/content-engine/trigger-daily`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated: ${data.topic?.title || 'Topic processed'}`);
        await Promise.all([fetchDashboard(), fetchTopics(), fetchReview()]);
      }
    } catch (e) { toast.error(e.message); }
    finally { setActionLoading(''); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="w-4 h-4" /></Button>
              <h1 className="text-lg font-bold text-gray-900">Content Engine</h1>
            </div>
            <div className="flex items-center gap-2">
              {['dashboard', 'topics', 'review', 'calendar'].map(v => (
                <Button key={v} size="sm" variant={view === v ? 'default' : 'ghost'}
                  onClick={() => setView(v)} className="text-xs capitalize"
                  data-testid={`nav-${v}`}>
                  {v === 'dashboard' && <BarChart3 className="w-3.5 h-3.5 mr-1" />}
                  {v === 'topics' && <Database className="w-3.5 h-3.5 mr-1" />}
                  {v === 'review' && <Shield className="w-3.5 h-3.5 mr-1" />}
                  {v === 'calendar' && <Calendar className="w-3.5 h-3.5 mr-1" />}
                  {v} {v === 'review' && reviewQueue.length > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{reviewQueue.length}</span>}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ==================== DASHBOARD VIEW ==================== */}
        {view === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-stats">
              {[
                { label: 'Total Topics', value: dashboard.total_topics, icon: Database, color: 'blue' },
                { label: 'Published', value: dashboard.statuses?.published || 0, icon: CheckCircle, color: 'green' },
                { label: 'In Queue', value: dashboard.statuses?.queued || 0, icon: Clock, color: 'blue' },
                { label: 'Review Needed', value: dashboard.review_required_count, icon: AlertTriangle, color: 'orange' },
                { label: 'FDI Topics', value: dashboard.fdi_total, icon: Globe, color: 'emerald' },
                { label: 'Stale Content', value: dashboard.stale_content_count, icon: RefreshCw, color: 'red' },
              ].map((s, i) => (
                <Card key={t.topic_id || `topic-${i}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between mb-1">
                      <s.icon className={`w-4 h-4 text-${s.color}-500`} />
                      <span className="text-xl font-bold">{s.value}</span>
                    </div>
                    <p className="text-[11px] text-gray-500">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Today + Quick Actions */}
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="text-sm">Today's Pipeline</CardTitle></CardHeader>
                <CardContent>
                  {dashboard.todays_post ? (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-800">Published Today</span>
                      </div>
                      <p className="text-sm text-gray-700">{dashboard.todays_post.title}</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800 mb-2">No post published today yet</p>
                      <Button size="sm" onClick={triggerDaily} disabled={actionLoading === 'daily'} data-testid="trigger-daily-btn"
                        className="bg-amber-600 hover:bg-amber-700 text-white">
                        {actionLoading === 'daily' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                        Generate & Publish Now
                      </Button>
                    </div>
                  )}
                  {dashboard.next_topic && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium mb-1">Next in Queue (Score: {dashboard.next_topic.total_score})</p>
                      <p className="text-sm text-gray-800">{dashboard.next_topic.title}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-sm">Content Mix</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-xs"><span>Auto-Publish</span><span className="font-bold text-green-600">{dashboard.auto_publish_count}</span></div>
                  <div className="flex justify-between text-xs"><span>Review Required</span><span className="font-bold text-orange-600">{dashboard.review_required_count}</span></div>
                  <div className="flex justify-between text-xs"><span>Quick Wins</span><span className="font-bold">{dashboard.types?.quick_win || 0}</span></div>
                  <div className="flex justify-between text-xs"><span>Authority</span><span className="font-bold">{dashboard.types?.authority || 0}</span></div>
                  <div className="flex justify-between text-xs"><span>FDI Published</span><span className="font-bold text-emerald-600">{dashboard.fdi_published}</span></div>
                </CardContent>
              </Card>
            </div>

            {/* Bucket Distribution */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Topic Buckets</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {sortedBuckets.map(([bucket, count]) => (
                    <button key={bucket} onClick={() => { setFilterBucket(bucket); setView('topics'); }}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-medium transition-colors">
                      {BUCKET_LABELS[bucket] || bucket} <span className="text-gray-500 ml-1">{count}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 30-Day Queue */}
            <Card>
              <CardHeader><CardTitle className="text-sm">30-Day Queue ({(dashboard.queue_30d || []).length} topics)</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {(dashboard.queue_30d || []).map((t, i) => (
                    <div key={g.slug || `gap-${i}`} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 text-xs">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-gray-400 w-5">{i + 1}</span>
                        <span className="truncate text-gray-800">{t.title}</span>
                        {t.fdi_related && <Globe className="w-3 h-3 text-emerald-500 flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="outline" className="text-[10px] py-0">{t.publish_mode === 'auto_publish' ? 'Auto' : 'Review'}</Badge>
                        <span className="font-mono text-gray-500 w-6 text-right">{t.total_score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ==================== TOPICS VIEW ==================== */}
        {view === 'topics' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center" data-testid="topic-filters">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search topics..." className="pl-9 h-9" data-testid="topic-search" />
              </div>
              <select value={filterBucket} onChange={e => setFilterBucket(e.target.value)} className="h-9 border rounded-lg px-2 text-xs">
                <option value="">All Buckets</option>
                {Object.entries(BUCKET_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-9 border rounded-lg px-2 text-xs">
                <option value="">All Status</option>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={filterFdi} onChange={e => setFilterFdi(e.target.value)} className="h-9 border rounded-lg px-2 text-xs">
                <option value="">All</option>
                <option value="true">FDI Only</option>
                <option value="false">Non-FDI</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => { setSearch(''); setFilterBucket(''); setFilterStatus(''); setFilterFdi(''); }} className="text-xs h-9">Clear</Button>
              <Button size="sm" onClick={fetchTopics} className="text-xs h-9"><RefreshCw className="w-3 h-3" /></Button>
              <span className="text-xs text-gray-500">{total} topics</span>
            </div>

            {/* Topics Table */}
            <div className="bg-white rounded-xl border overflow-x-auto">
              <table className="w-full text-xs" data-testid="topics-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2.5 px-3 font-medium text-gray-500">Topic</th>
                    <th className="text-center py-2.5 px-2 font-medium text-gray-500 w-14">Score</th>
                    <th className="text-center py-2.5 px-2 font-medium text-gray-500 w-20">Status</th>
                    <th className="text-center py-2.5 px-2 font-medium text-gray-500 w-20">Mode</th>
                    <th className="text-center py-2.5 px-2 font-medium text-gray-500 w-20">Bucket</th>
                    <th className="text-center py-2.5 px-2 font-medium text-gray-500 w-16">Risk</th>
                    <th className="text-right py-2.5 px-3 font-medium text-gray-500 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map(t => {
                    const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.idea;
                    return (
                      <tr key={t.topic_id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`topic-row-${t.topic_id}`}>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            {t.fdi_related && <Globe className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate max-w-md">{t.title}</p>
                              <p className="text-[10px] text-gray-400 truncate">{t.primary_keyword}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-2.5 px-2">
                          <span className={`font-bold ${t.total_score >= 70 ? 'text-emerald-600' : t.total_score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                            {t.total_score}
                          </span>
                        </td>
                        <td className="text-center py-2.5 px-2"><Badge className={`${sc.color} text-[10px] py-0`}>{sc.label}</Badge></td>
                        <td className="text-center py-2.5 px-2">
                          <Badge variant="outline" className={`text-[10px] py-0 ${t.publish_mode === 'auto_publish' ? 'border-green-300 text-green-700' : 'border-orange-300 text-orange-700'}`}>
                            {t.publish_mode === 'auto_publish' ? 'Auto' : 'Review'}
                          </Badge>
                        </td>
                        <td className="text-center py-2.5 px-2 text-[10px] text-gray-600">{BUCKET_LABELS[t.topic_bucket] || t.topic_bucket}</td>
                        <td className="text-center py-2.5 px-2">
                          <span className={`text-[10px] font-medium ${t.risk_level === 'high' ? 'text-red-600' : t.risk_level === 'medium' ? 'text-amber-600' : 'text-green-600'}`}>
                            {t.risk_level || 'low'}
                          </span>
                        </td>
                        <td className="text-right py-2.5 px-3">
                          <div className="flex items-center justify-end gap-1">
                            {t.status === 'queued' && (
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-purple-600"
                                disabled={actionLoading === t.topic_id + 'generate'}
                                onClick={() => handleAction(t.topic_id, 'generate')}>
                                {actionLoading === t.topic_id + 'generate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                              </Button>
                            )}
                            {t.status === 'approved' && (
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-green-600"
                                disabled={actionLoading === t.topic_id + 'publish'}
                                onClick={() => handleAction(t.topic_id, 'publish')}>
                                <Play className="w-3 h-3" />
                              </Button>
                            )}
                            {t.status === 'awaiting_review' && (
                              <>
                                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-green-600"
                                  onClick={() => handleAction(t.topic_id, 'review', { action: 'approve' })}>
                                  <CheckCircle className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-red-600"
                                  onClick={() => handleAction(t.topic_id, 'review', { action: 'reject', rejection_reason: 'Needs revision' })}>
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ==================== REVIEW QUEUE ==================== */}
        {view === 'review' && (
          <ReviewQueueView
            reviewQueue={reviewQueue}
            actionLoading={actionLoading}
            handleAction={handleAction}
            fetchAll={() => Promise.all([fetchDashboard(), fetchTopics(), fetchReview()])}
          />
        )}

        {/* ==================== CALENDAR VIEW ==================== */}
        {view === 'calendar' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold" data-testid="calendar-title">Content Calendar</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(dashboard?.queue_30d || []).slice(0, 30).map((t, i) => {
                const date = new Date();
                date.setDate(date.getDate() + i);
                return (
                  <div key={c.slug || `comp-${i}`} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-gray-500">
                        {date.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <span className="text-xs font-mono text-gray-400">#{i + 1}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">{t.title}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{BUCKET_LABELS[t.topic_bucket] || t.topic_bucket}</Badge>
                      {t.fdi_related && <Globe className="w-3 h-3 text-emerald-500" />}
                      <span className="text-[10px] text-gray-400 ml-auto">{t.total_score}pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <Toaster />
    </div>
  );
};

export default ContentEngine;
